import { useEffect, useRef, useState } from "react";
import { ListBigNumber, SupportBoard } from "./components";
import { BigNumberSupport } from "./components/ListBigNumber";
import { LoadMoreButton, SelectOption } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { fetchOccurrences, fetchOccurrencesBigNumber, fetchSupportRegionals, fetchUsers } from "../../api/tas";
import { fetchSupportGroupsAvailableForAssignment } from "../../api/supportGroups";
import { IOccurrence, SupportViewType } from "./types";
import { useSearchParams } from "react-router";
import { CLEAR_SORT_OPTION_ID, parseSortOption } from "./components/SortOptionsSupport";
import { assignmentParamsToSelection, assignmentSelectionToParams } from "./constants";
import SettingsSidebar, { type SupportFilters } from "./components/SettingsSidebar";

interface Props {
    assetIds: string[]
}

const SupportPage = ({ assetIds }: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const type = (searchParams.get('type') as BigNumberSupport) || 'all';
    const [data, setData] = useState<IOccurrence[]>([]);
    const supportType = searchParams.get('supportType') || 'VOLKSTOTAL';
    const search = searchParams.get('search') || ''
    const dn = searchParams.get('dn') || '';
    const responsible = searchParams.get('responsible') || '';
    const subject = searchParams.get('subject') || '';
    const subtopic = searchParams.get('subtopic') || '';
    const startDate = searchParams.get('startDate') || '';
    const endDate = searchParams.get('endDate') || '';
    const savedFilterStatus = searchParams.get('savedFilterStatus') || 'all';
    const [total, setTotal] = useState(0);
    const [size, setSize] = useState(30);
    const [isLoading, setIsLoading] = useState(true);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [viewType, setViewType] = useState<SupportViewType>("kanban");
    const [optionsRegionals, setOptionsRegionals] = useState<SelectOption[]>([]);
    const [optionsAssigned, setOptionsAssigned] = useState<SelectOption[]>([]);
    const [optionsGroups, setOptionsGroups] = useState<SelectOption[]>([]);
    const regional = searchParams.get('regional') || '';
    const assigned = searchParams.get('assigned') || '';
    const assignedToId = searchParams.get('assignedToId') || '';
    const assignedGroupId = searchParams.get('assignedGroupId') || '';
    const myGroupQueue = searchParams.get('myGroupQueue') === 'true';
    const assignedSelection = assignmentParamsToSelection(assigned, assignedToId);
    const sortBy = searchParams.get('sortBy') || '';
    const sortDirection = searchParams.get('sortDirection') || '';
    const [bigNumber, setBigNumber] = useState({
        OPEN: 0,
        IN_ANALYSIS: 0,
        ANSWERED: 0,
        FINISHED: 0,
    });

    const intl = useIntl();

    const fetchList = async () => {
        try {
            const res = await fetchOccurrences({
                status: type === 'all' ? null : type,
                page: 0,
                size: size,
                searchTerm: search,
                supportType: supportType === 'all' ? null : supportType,
                dn: dn,
                responsible: responsible,
                regional: regional === 'all' ? null : regional,
                subject: subject === 'all' ? null : subject || null,
                subtopic: subtopic || null,
                startDate: startDate || null,
                endDate: endDate || null,
                assigned: assigned || undefined,
                assignedToId: assignedToId || undefined,
                sortBy: sortBy || null,
                sortDirection: sortDirection || null,
                assignedGroupId: assignedGroupId || undefined,
                myGroupQueue,
                savedFilterStatus,
            });
            setData(res.content);
            setTotal(res.totalElements);

        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchBigNumberData = async () => {
        const res = await fetchOccurrencesBigNumber({
            searchTerm: search,
            supportType: supportType === 'all' ? null : supportType,
            dn: dn,
            responsible: responsible,
            regional: regional === 'all' ? null : regional,
            subject: subject === 'all' ? null : subject || null,
            subtopic: subtopic || null,
            startDate: startDate || null,
            endDate: endDate || null,
            assigned: assigned || undefined,
            assignedToId: assignedToId || undefined,
            assignedGroupId: assignedGroupId || undefined,
            myGroupQueue,
            savedFilterStatus,
        });
        setBigNumber({
            OPEN: res.OPEN.count,
            IN_ANALYSIS: res.IN_ANALYSIS.count,
            ANSWERED: res.ANSWERED.count,
            FINISHED: res.FINISHED.count,
        });
    }


    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const updateParam = (key: string, value: string) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            if (!value) {
                params.delete(key);
            } else {
                params.set(key, value);
            }

            return params;
        });
    };

    const handleSearch = (text: string) => {
        updateParam('search', text);
    };

    const handleListDebounce = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            fetchList();
            fetchBigNumberData();
        }, 500);
    };

    const handleDn = (text: string) => {
        updateParam('dn', text);
    };

    const handleSupportType = (value: string) => {
        updateParam('supportType', value);
    };

    const handleFilters = (filters: SupportFilters) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    params.set(key, value);
                } else {
                    params.delete(key);
                }
            });

            return params;
        });
    };

    const handleSort = (optionId: string) => {
        if (optionId === CLEAR_SORT_OPTION_ID) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                params.delete('sortBy');
                params.delete('sortDirection');
                return params;
            });
            return;
        }

        const { sortBy: newSortBy, sortDirection: newSortDirection } = parseSortOption(optionId);
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('sortBy', newSortBy);
            params.set('sortDirection', newSortDirection);
            return params;
        });
    };

    const handleAssignedChange = (values: string[]) => {
        const { assigned: nextAssigned, assignedToId: nextAssignedToId } = assignmentSelectionToParams(values);

        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            if (nextAssigned) {
                params.set('assigned', nextAssigned);
            } else {
                params.delete('assigned');
            }

            if (nextAssignedToId) {
                params.set('assignedToId', nextAssignedToId);
            } else {
                params.delete('assignedToId');
            }

            return params;
        });
    };

    const getTranslatedAssignmentFilterOptions = (): SelectOption[] => [
        {
            id: 'ASSIGNED',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.assigned' }),
        },
        {
            id: 'UNASSIGNED',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.unassigned' }),
        },
        {
            id: 'all',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignment.all' }),
        },
    ];

    const fetchRegionals = async () => {
        const res = await fetchSupportRegionals();
        if (res?.content) {
            const all = {
                id: 'all',
                label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.all' }),
            };

            const map = res?.content?.map((res: { id: number, name: string }) => {
                return {
                    id: res.id.toString(),
                    label: res.name,
                }
            })
            setOptionsRegionals([...map, all]);
        }
    };

    const fetchAssignedUsers = async () => {
        const users = await fetchUsers();
        const userOptions = users.map((user: {id: string, firstName: string, lastName: string}) => ({
            id: user.id,
            label: `${user.firstName} ${user.lastName}`,
        }));

        setOptionsAssigned([...getTranslatedAssignmentFilterOptions(), ...userOptions]);
    };

    const fetchGroups = async () => {
        const groups = await fetchSupportGroupsAvailableForAssignment();
        setOptionsGroups(groups.map(group => ({ id: String(group.id), label: group.name })));
    };

    const handleType = (newType: BigNumberSupport) => {
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('type', newType);
            return params;
        });
    };

    const onLoadMore = () => {
        const nextSize = size + 30;

        setSize(nextSize > total ? total : nextSize);
    }

    useEffect(() => {
        fetchRegionals();
        fetchAssignedUsers();
        fetchGroups();
    }, [intl.locale]);

    useEffect(() => {
        fetchList()
    }, [type, bigNumber, supportType, dn, responsible, size, regional, subject, subtopic, startDate, endDate, assigned, assignedToId, sortBy, sortDirection, assignedGroupId, myGroupQueue, savedFilterStatus]);

    useEffect(() => {
        fetchBigNumberData();
    }, [supportType, dn, responsible, regional, subject, subtopic, startDate, endDate, assigned, assignedToId, assignedGroupId, myGroupQueue, savedFilterStatus]);

    useEffect(() => {
        handleListDebounce();
    }, [search])


    useEffect(() => {
        if (!searchParams.get('supportType')) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                if (!params.get('supportType')) params.set('supportType', 'VOLKSTOTAL');
                if (!params.get('type')) params.set('type', 'all');
                if (!params.get('regional')) params.set('regional', 'all');
                if (!params.get('subject')) params.set('subject', 'all');
                if (!params.get('savedFilterStatus')) params.set('savedFilterStatus', 'all');
                return params;
            }, { replace: true });
        }
    }, []);

    return (
        <div className="margin-y-20">
            <ListBigNumber
                selected={type}
                analyze={bigNumber.IN_ANALYSIS}
                dealer={bigNumber.ANSWERED}
                finished={bigNumber.FINISHED}
                pending={bigNumber.OPEN}
                handleType={handleType}
            />
            <SupportBoard
                isLoading={isLoading}
                data={data}
                search={search}
                dn={dn}
                optionsAssigned={optionsAssigned}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSearch={handleSearch}
                handleSupportType={handleSupportType}
                handleDn={handleDn}
                handleSort={handleSort}
                supportType={supportType}
                handleAssignedChange={handleAssignedChange}
                assignedSelection={assignedSelection}
                onOpenSettings={() => setIsSettingsOpen(true)}
                viewType={viewType}
                onViewTypeChange={setViewType}
            />
            <SettingsSidebar
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                responsible={responsible}
                regional={regional}
                subject={subject}
                subtopic={subtopic}
                startDate={startDate}
                endDate={endDate}
                assignedGroupId={assignedGroupId}
                myGroupQueue={myGroupQueue}
                savedFilterStatus={savedFilterStatus}
                supportType={supportType}
                optionsRegionals={optionsRegionals}
                optionsGroups={optionsGroups}
                onApply={handleFilters}
            />
            <LoadMoreButton
                loaded={data?.length < 30 ? data.length : size}
                total={total}
                onLoadMore={onLoadMore}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.allLoaded" })}
            />
        </div>
    )
};

export default SupportPage
